import { useValue } from "@legendapp/state/react";
import { AvatarDisplay } from "@repo/ui/components/avatar-display";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@repo/ui/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@repo/ui/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/ui/tooltip";
import { createLink } from "@tanstack/react-router";
import { useEffect } from "react";

import { OrgAvatar } from "~/components/org-avatar";
import { refreshSharedBoards, sharedBoards$ } from "~/stores/shared-boards";

const SidebarMenuButtonLink = createLink(SidebarMenuButton);

/**
 * **Shared with me** sidebar section. Lists boards the user has guest
 * access to via `board_members` (Phase 6). Hidden when empty.
 *
 * Cross-org: items here may belong to a different `activeOrganization$`
 * than the one currently selected. Clicking through navigates to
 * `/boards/$boardId`, which the route's `beforeLoad` resolves to the
 * read-only member variant.
 */
export const SharedWithMe = () => {
  const items = useValue(sharedBoards$.items);
  const loaded = useValue(sharedBoards$.loaded);

  useEffect(() => {
    refreshSharedBoards();
  }, []);

  if (!loaded || items.length === 0) return null;

  return (
    <SidebarGroup>
      <Collapsible
        defaultOpen
        className="group/collapsible group-data-[collapsible=icon]:hidden"
      >
        <CollapsibleTrigger
          nativeButton={false}
          render={
            <SidebarGroupLabel className="my-1 flex w-full cursor-pointer select-none items-center hover:bg-sidebar-accent hover:text-sidebar-accent-foreground" />
          }
        >
          <Tooltip>
            <TooltipTrigger render={<span className="select-none" />}>
              Shared with me
            </TooltipTrigger>
            <TooltipContent side="right">
              Boards other people have shared with you.
            </TooltipContent>
          </Tooltip>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenu>
            {items.map((entry) => (
              <SidebarMenuItem key={entry.boardId}>
                <SidebarMenuButtonLink
                  name="left"
                  to="/boards/$boardId"
                  params={{ boardId: entry.boardId }}
                  preload="intent"
                  activeProps={{ isActive: true }}
                >
                  <AvatarDisplay
                    avatar={entry.boardAvatar as never}
                    fallbackIcon="dashboard"
                  />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm">
                      {entry.boardName || "Untitled board"}
                    </span>
                    <span className="flex items-center gap-1 truncate text-muted-foreground text-xs">
                      <OrgAvatar
                        name={entry.organizationName}
                        logo={entry.organizationLogo}
                        className="size-3"
                      />
                      <span className="truncate">{entry.organizationName}</span>
                    </span>
                  </div>
                </SidebarMenuButtonLink>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </CollapsibleContent>
      </Collapsible>
    </SidebarGroup>
  );
};
